import React, { useEffect, useState, useRef, useCallback, memo } from "react";
import {
  Box,
  Text,
  VStack,
  Radio,
  RadioGroup,
  Switch,
  Button,
  Input,
  useColorModeValue,
  useColorMode,
  useToast,
  Flex,
  Tooltip,
  Image
} from "@chakra-ui/react";
import Select from 'react-select';
import { cqAPI } from "../../lib/Auth";
import _ from "lodash";
import dialogManager from "../DialogManager/DialogManager";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Memoized Input Component
const MemoizedInput = memo(({ id, value, onChange, placeholder, isReadOnly }) => {
  return (
    <Input
      id={id}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      isReadOnly={isReadOnly}
    />
  );
});

const GenericSettings = ({ configuration, endpoint, onClose, method = "post", viewOnly = false, supportBatching = false, autoSave = false, bid_uid = "", long_query = false }) => {
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const invalidInputRef = useRef(null);
  const toast = useToast();
  const { colorMode } = useColorMode();
  const tmp_method = long_query ? "post" : "get";

  const fetchSettings = async () => {
    if (!endpoint) { return }

    const results = supportBatching ?
      tmp_method === "get" ?
      await cqAPI.get(`${endpoint}?batch=true${bid_uid}&id=${configuration.map(section => section.inputs.map(input => input.id).join()).join()}`).then(res => res.data || []) :
      await cqAPI.post(`${endpoint}?batch=true${bid_uid}`, {id: configuration.map(section => section.inputs.map(input => input.id).join()).join() }).then(res => res.data || []) :
      await Promise.all(configuration
        .map((section) =>
          section.inputs.map((input) =>
            cqAPI.get(`${endpoint}?id=${input.id}`).then((res) => ({
              id: input.id,
              value: res.data,
            }))
          )
        )
        .flat());
        
    const settingsData = results.reduce((acc, { id, value }) => {
      acc[id] = value;
      return acc;
    }, {});

    setSettings(settingsData);
    setOriginalSettings(_.cloneDeep(settingsData));
  };

  useEffect(() => {
    fetchSettings();
  }, [endpoint, configuration]);

  const handleRadioChange = (id, value) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [id]: value,
    }));
  };

  const handleSwitchChange = (id, event) => {
    const isChecked = event.target.checked;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [id]: !!isChecked,
    }));
  };

  const handleInputChange = (id, event) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [id]: event.target.value,
    }));
  };

  const handleSelectChange = (id, selectedOptions, isMulti) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [id]: isMulti ? (selectedOptions ? selectedOptions.map(option => option.value) : []) : (selectedOptions.value),
    }));
  };


  const saveChanges = async () => {
    for (const section of configuration) {
      for (const input of section.inputs) {
        if (input.type === "input" && input.pattern) {
          const regex = new RegExp(input.pattern);
          if (!regex.test(settings[input.id] || '')) {
            invalidInputRef.current = input.id;
            toast({
              title: "Invalid input",
              description: `${input.name || input.id} is invalid. Please check your input.`,
              status: "error",
              duration: 3000,
              isClosable: true,
            });
            document.getElementById(input.id).focus();
            return;
          }
        }
      }
    }

    const changes = {};
    Object.keys(settings).forEach((key) => {
      if (!_.isEqual(settings[key], originalSettings[key])) {
        changes[key] = settings[key];
      }
    });

    const results = supportBatching ?
      await cqAPI[method](`${endpoint}?batch=true`, Object.keys(changes).map(key => ({ key, value: changes[key] }))) :
      await Promise.all(Object.keys(changes).map((key) =>
        endpoint ? cqAPI[method](`${endpoint}`, { key: key, value: changes[key] }) : Promise.resolve({ type: 'ok' })
      ));

    const errors = supportBatching ? [results].filter(res => res.msg) : results.filter((res) => res.msg);

    if (errors.length > 0) {
      dialogManager.open({
        onDeny: () => { },
        acceptText: "OK",
        body: errors.map((error) => error.msg).join('\n'),
        header: "Error",
      });
    } else {
      if (!autoSave){
        fetchSettings();
      }
    }
  };

  useEffect(() => {
    const changesDetected = !_.isEqual(settings, originalSettings);
    if (autoSave && changesDetected){
      saveChanges();
    }
    setHasChanges(changesDetected);
  }, [settings, originalSettings]);

  const bgColor = useColorModeValue("gray.100", "gray.700");
  const hintColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Box p={4} w="100%" align="left">
      {!autoSave &&
        <Flex textAlign="right" mt={4} p={4} position="sticky" top="0" zIndex={"200"} bgColor="grey" borderRadius="md">
          {endpoint && !viewOnly && (
            <Button colorScheme="blue" isDisabled={!hasChanges} onClick={saveChanges}>
              Save
            </Button>)
          }
          {onClose && (
            <Button onClick={onClose} marginLeft="10px">
              Close
            </Button>
          )}
        </Flex>}
      {configuration.map((section, index) => (
        <Box key={index} mb={4} p={4} bgColor={bgColor} borderRadius="md">
          <Text mb={4}>{section.title}</Text>
          <Text mb={4}>{section.description}</Text>
          {section.inputs.map((input, idx) => (
            <Box key={idx} mb={4}>
              {!(input.icon || input.custom_icon) && <Text mb={1}>{input.name}</Text>}
              {input.hint && <Text fontSize="sm" mb={2} color={hintColor}>{input.hint}</Text>}
              {input.type === "radio" && (
                <RadioGroup
                  onChange={(value) => handleRadioChange(input.id, value)}
                  value={settings[input.id] || ""}
                  isDisabled={viewOnly}
                >
                  <VStack align="start">
                    {input.options.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </VStack>
                </RadioGroup>
              )}
              {input.type === "switch" && (
                <Switch
                  isChecked={settings[input.id]}
                  onChange={(event) => handleSwitchChange(input.id, event)}
                  isDisabled={viewOnly}
                >
                </Switch>
              )}
              {input.type === "switchwt" && (
                <>
                  <Switch
                    isChecked={settings[input.id]}
                    onChange={(event) => handleSwitchChange(input.id, event)}
                    isDisabled={viewOnly}
                  >
                  </Switch>
                  {settings[input.id] !== undefined && (
                    <MemoizedInput
                      id={input.id}
                      value={typeof(settings[input.id]) === 'string'? settings[input.id] : ''}
                      onChange={(event) => handleInputChange(input.id, event)}
                      placeholder={`Enter ${input.name} details`}
                      isReadOnly={viewOnly}
                    />
                  )}
                </>
              )}
              {input.type === "input" && (
                <Flex align="center">
                  {input.icon && (
                    <Tooltip label={input.name}>
                    <FontAwesomeIcon icon={input.icon} style={{ marginRight: "8px" }} size="lg" />
                    </Tooltip>
                  )}
                  {input.custom_icon && (
                    <Tooltip label={input.name}>
                      <Image
                        src={input.custom_icon}
                        alt={input.name}
                        boxSize="16px"
                        mr="8px"
                      />
                    </Tooltip>
                  )}
                  <MemoizedInput
                    id={input.id}
                    value={settings[input.id]}
                    onChange={(event) => handleInputChange(input.id, event)}
                    placeholder={input.name}
                    isReadOnly={viewOnly}
                  />
                </Flex>
              )}
              {input.type === "select" && (
                <Select
                  styles={{
                    control: (base) => ({
                      ...base,
                      background: colorMode === "light" ? "#F7FAFC" : "#2D3748",
                      borderColor: colorMode === "light" ? "#CBD5E0" : "#4A5568",
                      "&:hover": {
                        borderColor: colorMode === "light" ? "#A0AEC0" : "#718096",
                      },
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: colorMode === "light" ? "black" : "gray.700",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      color: "#fff",
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: colorMode === "light" ? "#717274" : "white",
                      boxShadow: "0px 10px 20px rgba(0,0,0,0.2)",
                      border: "1px solid",
                      borderColor: colorMode === "light" ? "#717274" : "#718096",
                      borderRadius: "4px",
                      zIndex: 9999,
                    }),
                    option: (base, state) => ({
                      ...base,
                      color: colorMode === "light" ? "white" : "black",
                      backgroundColor: colorMode === "light" ? "#717274" : "white",
                    }),
                  }}
                  id={input.id}
                  value={input.options.filter(option => settings[input.id] && Array.isArray(settings[input.id]) ? settings[input.id].includes(option.value) : (settings[input.id] === option.value))}
                  onChange={(selectedOptions) => handleSelectChange(input.id, selectedOptions, input.isMulti)}
                  options={input.options}
                  isMulti={input.isMulti}
                  isDisabled={viewOnly}
                />
              )}
              {input.type === "text" && (
                <Flex align="center">
                  {input.icon && (
                    <Tooltip label={input.name}>
                      <FontAwesomeIcon icon={input.icon} style={{ marginRight: "8px" }} size="lg" />
                    </Tooltip>
                  )}
                  <Text
                    id={input.id}
                    placeholder={input.name}
                    ml={4}
                  >
                    {settings[input.id]}
                  </Text>
                </Flex>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default GenericSettings;
